// ============================================================================
// SELF-ASSIGN FACTORY
// ============================================================================
//
// Several universal action handlers ("assign this ticket/task/deal to me",
// "claim this communication") share the identical shape:
//   1. (optional) guard the row is the right kind of entity
//   2. resolve the logged-in user id (toast + bail if no session)
//   3. idempotency: if the field already points at the current user, toast + bail
//   4. write the field via context.onUpdateItem, then success toast
//
// This factory centralizes that flow so each call site is just its field name,
// cardinality, and copy. Behavior is identical to the previously hand-written
// handlers (same toasts, same idempotency checks, same onUpdateItem payloads).

import type { DetailActionContext } from "@/contexts/DetailActionContext/types";
import type { BaseEntity } from "@/lib/types/table/base-types";
import { getBrowserSupabaseClient } from "@west-tennessee-consulting/supabase";

type ActionContext = DetailActionContext<BaseEntity>;

/**
 * Resolves the current Supabase user id. Emits the standard "Could not resolve
 * your session." error toast and returns null when there is no authenticated
 * user — callers should bail on null.
 */
export async function resolveCurrentUserId(
  context: ActionContext,
): Promise<string | null> {
  const supabase = getBrowserSupabaseClient();
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user?.id) {
    context.onToast("Could not resolve your session.", "error");
    return null;
  }
  return user.id;
}

interface SelfAssignMessages {
  /** Shown (info) when the field already points at the current user. */
  alreadySet: string;
  /** Shown (success) after a successful write. */
  success: string;
}

interface SelfAssignConfig {
  /** Row column to write — scalar user id, or a user-id array member. */
  field: string;
  /**
   * "scalar" → the field IS the user id (e.g. tickets.assigned_to,
   * communications.user_id). "array" → the field is a user-id list the user is
   * appended to (e.g. assigned_users on tasks/deals).
   */
  mode: "scalar" | "array";
  messages: SelfAssignMessages;
  /**
   * Optional pre-check run BEFORE resolving the user. Return false to abort
   * (the guard is responsible for emitting its own toast). Used by the
   * communication-attribution action to reject non-communication rows.
   */
  guard?: (row: Record<string, unknown>, context: ActionContext) => boolean;
}

/**
 * Builds a universal action handler that assigns the current user to a row
 * field (scalar) or appends them to a user-id array field.
 */
export function selfAssign(
  config: SelfAssignConfig,
): (item: Record<string, unknown>, context: ActionContext) => Promise<void> {
  return async (item, context) => {
    const row = item;

    if (config.guard && !config.guard(row, context)) return;

    const userId = await resolveCurrentUserId(context);
    if (!userId) return;

    if (config.mode === "scalar") {
      if (row[config.field] === userId) {
        context.onToast(config.messages.alreadySet, "info");
        return;
      }
      await context.onUpdateItem({ [config.field]: userId });
      context.onToast(config.messages.success, "success");
      return;
    }

    const current = Array.isArray(row[config.field])
      ? (row[config.field] as string[])
      : [];
    if (current.includes(userId)) {
      context.onToast(config.messages.alreadySet, "info");
      return;
    }
    await context.onUpdateItem({ [config.field]: [...current, userId] });
    context.onToast(config.messages.success, "success");
  };
}
